import React from 'react';
import { Modal } from '@mantine/core';
import type { BrandWithUrl } from '../../../models/Brand.ts';
import {
  UpdateBrandImageRequest,
  UpdateBrandMetadataRequest,
} from '../../../api/requests/brand-request.ts';
import useUpdateBrandImage from '../../../hooks/brands/useUpdateBrandImage.tsx';
import useUpdateBrandMetadata from '../../../hooks/brands/useUpdateBrandMetadata.ts';
import MutateBrandForm, {
  MutateBrandFormValues,
} from '../../ui/brands/MutateBrandForm.tsx';
import { notifyError } from '../../../utils/NotifyError.tsx';
import { useMutation } from '@tanstack/react-query';
import { ErrorResponse } from '../../../api/responses/error-response.ts';

interface UpdateBrandMetadataModalProps {
  opened: boolean;
  onClose: () => void;
  originalBrand: BrandWithUrl;
  onSuccess?: () => void;
}

const UpdateBrandMetadataModal = ({
  opened,
  onClose,
  originalBrand,
  onSuccess,
}: UpdateBrandMetadataModalProps) => {
  const { mutateAsync: updateBrandMetadata } = useUpdateBrandMetadata();
  const { mutateAsync: updateBrandImage } = useUpdateBrandImage();

  const brandId = originalBrand.id;

  const { mutate: handleSubmit, isPending } = useMutation<
    void,
    ErrorResponse,
    MutateBrandFormValues
  >({
    mutationFn: async (formValues: MutateBrandFormValues) => {
      const updateBrandMetadataRequest: UpdateBrandMetadataRequest = {
        brandId: brandId,
        name: formValues.name.trim(),
        services: formValues.services,
      };
      await updateBrandMetadata(updateBrandMetadataRequest);
      if (formValues.uploadedFiles.length > 0) {
        const updateBrandImageRequest: UpdateBrandImageRequest = {
          brandId: brandId,
          file: formValues.uploadedFiles[0],
        };
        await updateBrandImage(updateBrandImageRequest);
      }
    },
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      if (error.code === 'BRAND_NAME_ALREADY_EXISTS') {
        notifyError('更新品牌失敗', '品牌名稱已存在');
      } else {
        notifyError('更新品牌失敗', error.message);
      }
    },
  });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="更新品牌"
      centered
      closeOnClickOutside={false}
      styles={{ title: { fontWeight: 700, fontSize: 16 } }}
    >
      <MutateBrandForm
        initialValues={{
          name: originalBrand.name,
          services: originalBrand.services,
          originalFiles: [originalBrand.currentSignedUrl],
        }}
        onSubmit={handleSubmit}
        onCancel={onClose}
        isSubmitting={isPending}
        submitButtonText="更新"
      />
    </Modal>
  );
};

export { UpdateBrandMetadataModal };
